import { useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Context } from 'react';
import { IdentityContextType, AuthorizationConfig } from '../types';
import { createDefaultSafeRedirectUrl, createPassthroughSafeRedirectUrl } from '../utils/urlSecurity';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

// Default loading component
const DefaultLoadingComponent = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

// Default unauthorized component
const DefaultUnauthorizedComponent = () => (
  <Box sx={{ textAlign: 'center', mt: 4 }}>
    <h1>Unauthorized</h1>
    <p>You don't have permission to access this resource.</p>
  </Box>
);

export function createAuthorizationHOC<TRole>(
  identityContext: Context<IdentityContextType<TRole>>,
  config: AuthorizationConfig<TRole> = {}
) {
  const {
    unauthorizedUrl = '/unauthorized',
    LoadingComponent = DefaultLoadingComponent,
    UnauthorizedComponent = DefaultUnauthorizedComponent,
    getSafeRedirectUrl,
    allowedRedirectUrls,
  } = config;

  // Create a safe redirect function based on configuration
  const safeRedirectFn = (() => {
    if (getSafeRedirectUrl) {
      // Use provided function
      return getSafeRedirectUrl;
    } else if (allowedRedirectUrls && allowedRedirectUrls.length > 0) {
      // Create restricted function with allowedRedirectUrls
      return createDefaultSafeRedirectUrl(allowedRedirectUrls);
    } else {
      // No restrictions - allow all URLs (passthrough)
      return createPassthroughSafeRedirectUrl();
    }
  })();

  return function withAuthorization<P extends object>(
    WrappedComponent: React.ComponentType<P>,
    allowedRoles: TRole[]
  ): React.FC<P> {
    return (props: P) => {
      const { assignedRoles, loadingRoles } = useContext(identityContext);
      const [hasAccess, setHasAccess] = useState<boolean | null>(null);
      const navigate = useNavigate();

      useEffect(() => {
        if (!loadingRoles) {
          if (allowedRoles.length === 0) {
            setHasAccess(true);
          } else {
            setHasAccess(assignedRoles?.some((role) => allowedRoles.includes(role)) ?? false);
          }
        }
      }, [assignedRoles, loadingRoles]);

      useEffect(() => {
        // Navigate to unauthorized page when access is denied
        if (hasAccess === false) {
          // Store the current URL so user can retry after getting access
          const currentPath = window.location.pathname + window.location.search;
          const safeUrl = safeRedirectFn(currentPath, '/');
          sessionStorage.setItem('unauthorized_redirect_url', safeUrl);
          navigate(unauthorizedUrl);
        }
      }, [hasAccess, navigate]); // Remove safeRedirectFn from dependencies since it's stable

      if (loadingRoles) {
        return <LoadingComponent />;
      }

      if (hasAccess === null) {
        return null;
      }

      if (hasAccess) {
        return <WrappedComponent {...props} />;
      } else {
        return <UnauthorizedComponent />;
      }
    };
  };
}
